import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { BsPrinter, BsFileEarmarkText, BsFileEarmarkRuled, BsFileEarmarkCheck } from "react-icons/bs";
import {
  initialMemberDetails,
  initialNomineeDetails,
  initialMemberBusinessDetails,
  initialNomineeBusinessDetails,
  initialLoanDetails,
  initialProposedLoanDetails,
  initialBankDetails,
} from "../../utils/utils";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { useAuth } from "../../components/shared/AuthContext";
import MemberDetails from "./MemberDetails";
import PersonalDetails from "../Customers/PersonalDetails";
import VerificationDetails from "./VerificationDetails";
import NomineeDetails from "../Customers/NomineeDetails";
import MemberBusinessDetails from "./MemberBusinessDetails";
import NomineeBusinessDetails from "./NomineeBusinessDetails";
import LoanDetails from "./LoanDetails";
import ProposedLoanDetails from "./ProposedLoanDetails";
import BankDetails from "../Customers/BankDetails";
import BusinessLoanPrintView from "./BusinessLoanPrintView";
import LoanAgreementView from "./LoanAgreementView";
import PromissoryNoteView from "./PromissoryNoteView";
import SanctionLetterView from "./SanctionLetterView";
import MemberPhotos from "./MemberPhotos";
import NomineePhotos from "./NomineePhotos";

const steps = [
  "Personal Details",
  "Verification Details",
  "Member Details",
  "Nominee Details",
  "Member Business Details",
  "Nominee Business Details",
  "Member Photos",
  "Nominee Photos",
  "Loan Details",
  "Proposed Loan Details",
  "Bank Details",
];

// Live data holds both 'Approved' and 'approved', and roleName has historically
// carried 'super admin' alongside 'superadmin' (login.js:26-35 documents that as
// a real bug that silently disabled the highest-privilege role). Compare
// normalised, never raw.
const norm = (v) => String(v || "").toLowerCase().trim();

const AddEditBusinessLoan = () => {
  const location = useLocation();
  const { type, stepNo, memberId, customerId } = location.state || {};
  const { roleName, permissions } = useAuth();
  const [memberCustomerId, setMemberCustomerId] = useState(customerId || null);
  const [activeStep, setActiveStep] = useState(stepNo || 0);
  const [skipped, setSkipped] = useState(new Set());
  const [personalDetailsData, setPersonalDetailsData] = useState({});
  const [verificationDetailsData, setVerificationDetailsData] = useState({});
  const [memberDetailsData, setMemberDetailsData] = useState(initialMemberDetails);
  // Whether a member_details ROW WAS ACTUALLY LOADED, which `memberDetailsData`
  // cannot tell us: it is seeded with `initialMemberDetails` (24 keys), so the
  // `Object.keys(...).length === 0` test below could never fire and a customer
  // with no row was still treated as an Edit — POSTing type:"Edit" with no id
  // and getting back 400 "Update requires a valid ID." with no way forward.
  //
  // A separate flag rather than seeding the state to `{}`: those 24 defaults are
  // load-bearing. `stageOwnerCanEdit()` reads dataEntryOperatorStatus/
  // branchManagerStatus/adminStatus from this object, and with `{}` they
  // normalise to "" — no stage is open, `editable` is false, and lockType()
  // turns every tab read-only for the Data Entry Operator.
  const [memberRowExists, setMemberRowExists] = useState(false);
  const [nomineeDetailsData, setNomineeDetailsData] = useState({});
  const [memberBusinessDetailsData, setMemberBusinessDetailsData] = useState(initialMemberBusinessDetails);
  const [nomineeBusinessDetailsData, setNomineeBusinessDetailsData] = useState(initialNomineeBusinessDetails);
  const [loanDetailsData, setLoanDetailsData] = useState(initialLoanDetails);
  const [proposedLoanDetailsData, setProposedLoanDetailsData] = useState(initialProposedLoanDetails);
  const [bankDetailsData, setBankDetailsData] = useState({});
  const [memberPhotosData, setMemberPhotosData] = useState({});
  const [nomineePhotosData, setNomineePhotosData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showPrintView, setShowPrintView] = useState(false);
  const [showLoanAgreement, setShowLoanAgreement] = useState(false);
  const [showPromissoryNote, setShowPromissoryNote] = useState(false);
  const [showSanctionLetter, setShowSanctionLetter] = useState(false);

  const getMembersData = async (memberId) => {
    try {
      // Fetch member_details, business details, loan details, and photos in parallel
      const [
        memberRes,
        memberBusinessRes,
        nomineeBusinessRes,
        loanRes,
        proposedLoanRes,
        memberPhotosRes,
        nomineePhotosRes
      ] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/getModelData/member_details?filter={"id":${memberId}}`),
        axios.get(`${API_BASE_URL}/api/getModelData/member_business_details?filter={"memberId":${memberId}}`),
        axios.get(`${API_BASE_URL}/api/getModelData/nominee_business_details?filter={"memberId":${memberId}}`),
        axios.get(`${API_BASE_URL}/api/getModelData/loan_details?filter={"memberId":${memberId}}`),
        axios.get(`${API_BASE_URL}/api/getModelData/proposed_loan_details?filter={"memberId":${memberId}}`),
        axios.get(`${API_BASE_URL}/api/getModelData/member_photos?filter={"memberId":${memberId}}`),
        axios.get(`${API_BASE_URL}/api/getModelData/nominee_photos?filter={"memberId":${memberId}}`)
      ]);

      const memberDetails = memberRes.data.data[0] || {};
      console.log("Fetched Member Details:", memberDetails);

      // `id` rather than truthiness: the fallback above is `{}`, and an empty
      // object is truthy. This is the only reliable "did a row come back".
      setMemberRowExists(Boolean(memberDetails.id));
      // Keep the defaults when nothing loaded, so the form still has its 24
      // starting values (and the stage statuses editability depends on).
      setMemberDetailsData(
        memberDetails.id ? memberDetails : initialMemberDetails
      );
      setMemberCustomerId(memberDetails.customerId || memberCustomerId);
      setMemberBusinessDetailsData(memberBusinessRes.data.data[0] || {});
      setNomineeBusinessDetailsData(nomineeBusinessRes.data.data[0] || {});
      setLoanDetailsData(loanRes.data.data[0] || {});
      setProposedLoanDetailsData(proposedLoanRes.data.data[0] || {});
      setMemberPhotosData(memberPhotosRes.data.data[0] || {});
      setNomineePhotosData(nomineePhotosRes.data.data[0] || {});
    } catch (error) {
      console.error("Error fetching member data:", error);
    }
  };

  const getCustomersData = async (customerId) => {
    try {
      // Fetch personal, verification, nominee, and bank details in parallel
      const [
        personalRes,
        verificationRes,
        nomineeRes,
        bankRes
      ] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/getModelData/personal_details?filter={"id":${customerId}}`),
        axios.get(`${API_BASE_URL}/api/getModelData/verification_details?filter={"customerId":${customerId}}`),
        axios.get(`${API_BASE_URL}/api/getModelData/nominee_details?filter={"customerId":${customerId}}`),
        axios.get(`${API_BASE_URL}/api/getModelData/bank_details?filter={"customerId":${customerId}}`)
      ]);

      console.log("Fetched Personal Details:", personalRes.data.data[0]);

      setPersonalDetailsData(personalRes.data.data[0] || {});
      setVerificationDetailsData(verificationRes.data.data[0] || {});
      setNomineeDetailsData(nomineeRes.data.data[0] || {});
      setBankDetailsData(bankRes.data.data[0] || {});
    } catch (error) {
      console.error("Error fetching customer data:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      // `memberId` is required: the page can be entered on the customerId path
      // (from the Pending / Requested / Verified tabs), and without this the
      // filter below became the literal {"id":undefined}, which is not valid
      // JSON. getModelData.js:98 JSON.parse then threw and answered 500, caught
      // and logged by getMembersData — a guaranteed-failing request whose only
      // visible effect was a console error.
      if ((type === "View" || type === "Edit") && memberId) {
        await getMembersData(memberId);
      }
      if (memberCustomerId) {
        await getCustomersData(memberCustomerId);
      }
      setIsLoading(false);
    };
    fetchData();
  }, [type, memberId, memberCustomerId]);


  // Auto-fill names from the personal and nominee records.
  //
  // Runs for Add, View AND Edit. It used to be gated to `type === "Add"`, which
  // meant a saved record never filled these in — and every existing row has
  // name null or "" (nobody typed it by hand), so on View the field stayed
  // blank forever while the customer's name sat one join away.
  //
  // `prev.<field> ||` is what makes this safe on a saved record: a stored value
  // always wins, so this can only ever fill a genuinely empty field and can
  // never overwrite one. The saved record arrives asynchronously, and the effect
  // re-runs when personalDetailsData resolves, so ordering does not matter.
  useEffect(() => {
    setProposedLoanDetailsData((prev) => ({
      ...prev,
      applicantName: prev.applicantName || personalDetailsData?.name || "",
      coApplicant1: prev.coApplicant1 || nomineeDetailsData?.name || "",
    }));

    // The business record belongs to the customer, so its Name is the
    // customer's — typing it a second time only creates a way for the two to
    // disagree.
    setMemberBusinessDetailsData((prev) => ({
      ...prev,
      name: prev.name || personalDetailsData?.name || "",
    }));

    setNomineeBusinessDetailsData((prev) => ({
      ...prev,
      name: prev.name || nomineeDetailsData?.name || "",
    }));
  }, [personalDetailsData?.name, nomineeDetailsData?.name]);

  const isStepOptional = (step) => {
    return false;
  };

  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  const handleNext = () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
      throw new Error("You can't skip a step that isn't optional.");
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
  };

  const handlePrint = () => {
    if (isLoading) {
      alert("Data is still loading. Please wait a moment and try again.");
      return;
    }
    setShowPrintView(true);
    setShowLoanAgreement(false);
    setShowPromissoryNote(false);
    setShowSanctionLetter(false);
  };

  const handleLoanAgreement = () => {
    if (isLoading) {
      alert("Data is still loading. Please wait a moment and try again.");
      return;
    }
    setShowLoanAgreement(true);
    setShowPrintView(false);
    setShowPromissoryNote(false);
    setShowSanctionLetter(false);
  };

  const handlePromissoryNote = () => {
    if (isLoading) {
      alert("Data is still loading. Please wait a moment and try again.");
      return;
    }
    setShowPromissoryNote(true);
    setShowPrintView(false);
    setShowLoanAgreement(false);
    setShowSanctionLetter(false);
  };

  const handleSanctionLetter = () => {
    if (isLoading) {
      alert("Data is still loading. Please wait a moment and try again.");
      return;
    }
    setShowSanctionLetter(true);
    setShowPrintView(false);
    setShowLoanAgreement(false);
    setShowPromissoryNote(false);
  };

  const handleBack = () => {
    setShowPrintView(false);
    setShowLoanAgreement(false);
    setShowPromissoryNote(false);
    setShowSanctionLetter(false);
  };

  const handleStepBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Who owns the application at this moment.
  //
  // The application moves DEO -> Branch Manager -> Admin, and only the role whose
  // stage is still open may change it. Once a stage is submitted its owner drops
  // to read-only: a DEO cannot revise what they have already sent for review, and
  // a BM cannot revise what is already with the admin. Sending it back
  // ("retrigger") reopens the stage for its owner alone.
  const stageOwner = (() => {
    const deo = norm(memberDetailsData?.dataEntryOperatorStatus);
    const bm = norm(memberDetailsData?.branchManagerStatus);
    const admin = norm(memberDetailsData?.adminStatus);
    if ([deo, bm, admin].includes("rejected")) {
      return "nobody — this application was rejected";
    }
    if (["pending", "retrigger"].includes(deo)) return "the Data Entry Operator";
    if (deo === "submitted" && ["pending", "retrigger"].includes(bm)) return "the Branch Manager";
    if (deo === "submitted" && bm === "submitted" && ["pending", "retrigger"].includes(admin)) return "the Admin";
    return null; // Disbursed / completed
  })();

  const isDisbursed = stageOwner === null;

  const stageOwnerCanEdit = () => {
    const deo = norm(memberDetailsData?.dataEntryOperatorStatus);
    const bm = norm(memberDetailsData?.branchManagerStatus);
    const admin = norm(memberDetailsData?.adminStatus);
    const open = (s) => ["pending", "retrigger"].includes(s);

    if (roleName === "Data Entry Operator") return open(deo);
    if (roleName === "Branch Manager") return deo === "submitted" && open(bm);
    if (roleName === "admin") {
      return deo === "submitted" && bm === "submitted" && open(admin);
    }
    return false;
  };

  const hasGrantedEdit = Boolean(
    permissions?.some((p) => p.page === "businessLoan" && p.edit)
  );

  const isSuperAdmin = norm(roleName).replace(/\s+/g, "") === "superadmin";

  const editable =
    isSuperAdmin ||
    (!isDisbursed &&
      (stageOwnerCanEdit() ||
        (roleName === "Branch Manager" && hasGrantedEdit)));

  // Locked tabs reuse the existing read-only "View" path rather than adding a
  // second disabling mechanism beside it.
  const lockType = (computedType) => (editable ? computedType : "View");

  const renderStepContent = (step) => {
    if (isLoading) {
      return <Typography>Loading data...</Typography>;
    }
    switch (step) {
      case 0:
        return (
          <PersonalDetails
            personalDetailsData={personalDetailsData}
            setPersonalDetailsData={setPersonalDetailsData}
            // Aadhaar lives in verification_details, not personal_details.
            // The parent already fetched it (see getCustomersData); pass it so
            // the field prefills and the duplicate check knows who owns it.
            verificationDetailsData={verificationDetailsData}
            setVerificationDetailsData={setVerificationDetailsData}
            customerId={memberCustomerId}
            handleNext={handleNext}
            type={lockType(type === "View" ? type : "Edit")}
            locked={!editable}
            mode="businessLoan"
          />
        );
      case 1:
        return (
          <VerificationDetails
            customerId={memberCustomerId}
            verificationDetailsData={verificationDetailsData}
            setVerificationDetailsData={setVerificationDetailsData}
            handleNext={handleNext}
            type={lockType(type === "View" ? type : "Edit")}
            locked={!editable}
            mode="businessLoan"
          />
        );
      case 2:
        return (
          <MemberDetails
            customerId={memberCustomerId}
            memberDetailsData={memberDetailsData}
            setMemberDetailsData={setMemberDetailsData}
            handleNext={handleNext}
            type={lockType(
              // No row on file -> Add, so the button CREATES it. This used to
              // test Object.keys(memberDetailsData).length, which is never 0
              // because the state is seeded with 24 defaults, so a customer
              // with no member_details row was treated as an Edit and dead-ended
              // on 400 "Update requires a valid ID."
              !memberRowExists
                ? "Add"
                : type === "View"
                  ? "View"
                  : type
            )}
            locked={!editable}
          />
        );
      case 3:
        return (
          <NomineeDetails
            customerId={memberCustomerId}
            nomineeDetailsData={
              Object.keys(nomineeDetailsData).length === 0
                ? initialNomineeDetails
                : nomineeDetailsData
            }
            setNomineeDetailsData={setNomineeDetailsData}
            handleNext={handleNext}
            type={lockType(
              Object.keys(nomineeDetailsData).length === 0
                ? "Add"
                : type === "View"
                  ? "View"
                  : "Edit"
            )}
            locked={!editable}
            mode="businessLoan"
          />
        );
      case 4:
        return (
          <MemberBusinessDetails
            memberId={memberDetailsData.id}
            memberBusinessDetailsData={memberBusinessDetailsData}
            setMemberBusinessDetailsData={setMemberBusinessDetailsData}
            handleNext={handleNext}
            type={lockType(
              Object.keys(memberBusinessDetailsData).length === 0
                ? "Add"
                : type === "View"
                  ? "View"
                  : type
            )}
            locked={!editable}
          />
        );
      case 5:
        return (
          <NomineeBusinessDetails
            memberId={memberDetailsData?.id}
            nomineeId={nomineeDetailsData?.id}
            nomineeBusinessDetailsData={nomineeBusinessDetailsData}
            setNomineeBusinessDetailsData={setNomineeBusinessDetailsData}
            handleNext={handleNext}
            type={lockType(
              Object.keys(nomineeBusinessDetailsData).length === 0
                ? "Add"
                : type === "View"
                  ? "View"
                  : type
            )}
            locked={!editable}
          />
        );
      case 6:
        return (
          <MemberPhotos
            memberId={memberDetailsData?.id}
            memberPhotosData={memberPhotosData}
            setMemberPhotosData={setMemberPhotosData}
            personalDetailsData={personalDetailsData}
            verificationDetailsData={verificationDetailsData}
            handleNext={handleNext}
            type={lockType(
              Object.keys(memberPhotosData).length === 0
                ? "Add"
                : type === "View"
                  ? "View"
                  : type
            )}
            locked={!editable}
          />
        );
      case 7:
        return (
          <NomineePhotos
            memberId={memberDetailsData?.id}
            nomineeId={nomineeDetailsData?.id}
            nomineePhotosData={nomineePhotosData}
            setNomineePhotosData={setNomineePhotosData}
            nomineeDetailsData={nomineeDetailsData}
            handleNext={handleNext}
            type={lockType(
              Object.keys(nomineePhotosData).length === 0
                ? "Add"
                : type === "View"
                  ? "View"
                  : type
            )}
            locked={!editable}
          />
        );
      case 8:
        return (
          <LoanDetails
            memberId={memberDetailsData?.id}
            loanDetailsData={loanDetailsData}
            setLoanDetailsData={setLoanDetailsData}
            handleNext={handleNext}
            type={lockType(
              Object.keys(loanDetailsData).length === 0
                ? "Add"
                : type === "View"
                  ? "View"
                  : type
            )}
            locked={!editable}
          />
        );
      case 9:
        return (
          <ProposedLoanDetails
            memberId={memberDetailsData?.id}
            proposedLoanDetailsData={proposedLoanDetailsData}
            setProposedLoanDetailsData={setProposedLoanDetailsData}
            personalDetailsData={personalDetailsData}
            nomineeDetailsData={nomineeDetailsData}
            handleNext={handleNext}
            type={lockType(
              Object.keys(proposedLoanDetailsData).length === 0
                ? "Add"
                : type === "View"
                  ? "View"
                  : type
            )}
            locked={!editable}
          />
        );
      case 10:
        return (
          <BankDetails
            customerId={memberCustomerId}
            bankDetailsData={
              Object.keys(bankDetailsData).length === 0
                ? initialBankDetails
                : bankDetailsData
            }
            setBankDetailsData={setBankDetailsData}
            handleNext={handleNext}
            type={lockType(
              Object.keys(bankDetailsData).length === 0
                ? "Add"
                : type === "View"
                  ? "View"
                  : "Edit"
            )}
            locked={!editable}
            mode="businessLoan"
            // Read only for data_operator_status: finishing this step moves the
            // customer from the Requested Loan tab to Verified.
            personalDetailsData={personalDetailsData}
          />
        );
      default:
        return <Typography>Unknown Step</Typography>;
    }
  };

  return (
    <>
      {showPromissoryNote ? (
        <PromissoryNoteView
          personalDetailsData={personalDetailsData}
          loanDetailsData={loanDetailsData}
          memberDetails={memberDetailsData}
          proposedLoanDetailsData={proposedLoanDetailsData}
          onBack={handleBack}
        />
      ) : showSanctionLetter ? (
        <SanctionLetterView
          personalDetailsData={personalDetailsData}
          loanDetailsData={loanDetailsData}
          memberDetailsData={memberDetailsData}
          proposedLoanDetailsData={proposedLoanDetailsData}
          onBack={handleBack}
        />
      ) : showLoanAgreement ? (
        <LoanAgreementView
          personalDetailsData={personalDetailsData}
          loanDetailsData={loanDetailsData}
          proposedLoanDetailsData={proposedLoanDetailsData}
          onBack={handleBack}
        />
      ) : showPrintView ? (
        <BusinessLoanPrintView
          personalDetailsData={personalDetailsData}
          verificationDetailsData={verificationDetailsData}
          memberDetailsData={memberDetailsData}
          nomineeDetailsData={nomineeDetailsData}
          memberBusinessDetailsData={memberBusinessDetailsData}
          nomineeBusinessDetailsData={nomineeBusinessDetailsData}
          loanDetailsData={loanDetailsData}
          proposedLoanDetailsData={proposedLoanDetailsData}
          bankDetailsData={bankDetailsData}
          memberPhotosData={memberPhotosData}
          nomineePhotosData={nomineePhotosData}
          onBack={handleBack}
        />
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: 2,
              mb: 3,
              mt: { xs: 1, sm: 3 },
              px: { xs: 1, sm: 0 }
            }}
          >
            <h1 style={{
              fontSize: '1.5rem',
              margin: 0,
              fontWeight: 800,
              color: '#1e293b'
            }}>{type} Member</h1>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                justifyContent: { xs: "flex-start", sm: "flex-end" },
                width: { xs: "100%", sm: "auto" }
              }}
            >
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSanctionLetter}
                disabled={isLoading}
                startIcon={<BsFileEarmarkCheck size={14} />}
                size="small"
                sx={{
                  fontSize: '0.75rem',
                  py: 0.5,
                  px: 1,
                  height: '28px'
                }}
              >
                Sanction Letter
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleLoanAgreement}
                disabled={isLoading}
                startIcon={<BsFileEarmarkRuled size={14} />}
                size="small"
                sx={{
                  fontSize: '0.75rem',
                  py: 0.5,
                  px: 1,
                  height: '28px'
                }}
              >
                Loan Agreement
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={handlePromissoryNote}
                disabled={isLoading}
                startIcon={<BsFileEarmarkText size={14} />}
                size="small"
                sx={{
                  fontSize: '0.75rem',
                  py: 0.5,
                  px: 1,
                  height: '28px'
                }}
              >
                Promissory Note
              </Button>
              <Button
                className="print"
                variant="contained"
                color="secondary"
                onClick={handlePrint}
                disabled={isLoading}
                startIcon={<BsPrinter size={14} />}
                size="small"
                sx={{
                  fontSize: '0.75rem',
                  py: 0.5,
                  px: 1,
                  height: '28px'
                }}
              >
                Loan Application
              </Button>
            </Box>
          </Box>

          {/* Main UI */}
          <Box sx={{ width: "100%" }}>
            {/* Desktop Stepper */}
            <Box sx={{
              width: '100%',
              display: { xs: 'none', md: 'block' },
              pb: 2
            }}>
              <Stepper
                activeStep={activeStep}
                sx={{
                  "& .MuiStepLabel-label": { fontSize: "0.75rem", fontWeight: 500 },
                  "& .MuiStepIcon-root.Mui-active": { color: "#004BB5" },
                  "& .MuiStepIcon-root.Mui-completed": { color: "#10B981" },
                  "& .MuiStepLabel-label.Mui-active": { color: "#004BB5", fontWeight: 700 },
                  "& .MuiStepLabel-label.Mui-completed": { color: "#10B981" },
                }}
              >
                {steps.map((label, index) => {
                  const stepProps = {};
                  const labelProps = {};
                  if (isStepOptional(index)) {
                    labelProps.optional = (
                      <Typography variant="caption">Optional</Typography>
                    );
                  }
                  if (isStepSkipped(index)) {
                    stepProps.completed = false;
                  }
                  return (
                    <Step
                      key={label}
                      {...stepProps}
                      onClick={() => {
                        if (type === "View" && !isLoading) {
                          setActiveStep(index);
                        }
                      }}
                      style={{ cursor: type === "View" && !isLoading ? "pointer" : "default" }}
                    >
                      <StepLabel {...labelProps}>{label}</StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
            </Box>

            {/* Mobile Automated Stepper - NO SCROLL */}
            <Box sx={{
              display: { xs: 'block', md: 'none' },
              mb: 3,
              px: 1
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>
                  STEP {activeStep + 1} OF {steps.length}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 800, color: '#004BB5' }}>
                  {Math.round(((activeStep + 1) / steps.length) * 100)}% COMPLETE
                </Typography>
              </Box>

              <Box sx={{
                height: '4px',
                width: '100%',
                bgcolor: '#e2e8f0',
                borderRadius: '2px',
                mb: 2,
                overflow: 'hidden'
              }}>
                <Box sx={{
                  height: '100%',
                  width: `${((activeStep + 1) / steps.length) * 100}%`,
                  bgcolor: '#4f46e5',
                  transition: 'width 0.3s ease'
                }} />
              </Box>

              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                bgcolor: 'white',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: '1px solid #f1f5f9'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: '#004BB5',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: '0.875rem'
                  }}>
                    {activeStep + 1}
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>
                    {steps[activeStep]}
                  </Typography>
                </Box>
                {activeStep > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleStepBack}
                    sx={{
                      borderRadius: '8px',
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      py: 0.5,
                      borderColor: '#004BB5',
                      color: '#004BB5'
                    }}
                  >
                    BACK
                  </Button>
                )}
              </Box>
            </Box>
            {activeStep === steps.length ? (
              <React.Fragment>
                <Typography sx={{ mt: 2, mb: 1 }}>
                  All steps completed - you're finished
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                  <Box sx={{ flex: "1 1 auto" }} />
                </Box>
              </React.Fragment>
            ) : (
              <React.Fragment>
                {isDisbursed && !isSuperAdmin && (
                  <div className="mt-3 mb-1 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    <span aria-hidden="true">🔒</span>
                    <span>
                      <strong>This loan has been disbursed.</strong> The
                      application can no longer be changed. To correct it, raise
                      a new application.
                    </span>
                  </div>
                )}
                {/* The stage lock, which is not the disbursement lock: the loan
                    is simply with someone else right now. Says WHO, so the
                    operator knows whether to wait or to ask for a retrigger
                    rather than assuming the screen is broken. */}
                {!isDisbursed && !editable && (
                  <div className="mt-3 mb-1 flex items-start gap-2 rounded-lg border border-sky-300 bg-sky-50 px-4 py-3 text-sm text-sky-900">
                    <span aria-hidden="true">👁️</span>
                    <span>
                      <strong>View only.</strong> This application is with{" "}
                      {stageOwner}. It can be changed again once it is sent back
                      to you.
                    </span>
                  </div>
                )}
                <Box sx={{ mt: 2, mb: 1 }}>{renderStepContent(activeStep)}</Box>
                <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                  <Box sx={{ flex: "1 1 auto" }} />
                  {/* {isStepOptional(activeStep) && (
                    <Button color="inherit" onClick={handleSkip} sx={{ mr: 1 }}>
                      Skip
                    </Button>
                  )} */}
                </Box>
              </React.Fragment>
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default AddEditBusinessLoan;